import React, { useState, useEffect } from "react";
import { useParams, useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { API_BASE_URL } from "../../config/apiConfig";
import { CheckCircle, XCircle } from "lucide-react";

const PublicGroupInvitePage = () => {
    const { groupId, groupName } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();

    const userId = searchParams.get("user");
    const [status, setStatus] = useState("idle");
    const [message, setMessage] = useState("");

    const handleAction = async (action) => {
        setStatus("loading");
        try {
            const res = await fetch(`${API_BASE_URL}/api/groups/invite/${groupId}/${userId}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ action }),
            });

            const data = await res.json();
            if (res.ok) {
                setStatus("success");
                setMessage(data.message);
            } else {
                setStatus("error");
                setMessage(data.message || "Something went wrong.");
            }
        } catch {
            setStatus("error");
            setMessage("Network error. Please try again.");
        }
    };

    useEffect(() => {
        if (message.includes("already joined")) {
            const timer = setTimeout(() => {
                navigate("/login");
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [message, navigate]);

    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 text-center p-6">
            {status === "idle" && (
                <div>
                    <h1 className="text-2xl font-semibold mb-4">
                        Invitation to Join <span className="text-green-700">{decodeURIComponent(groupName)}</span>
                    </h1>
                    <p className="text-gray-600 mb-6">
                        You’ve been invited to join <strong>{decodeURIComponent(groupName)}</strong>.
                    </p>
                    <div className="flex gap-4 justify-center">
                        <button
                            onClick={() => handleAction("accept")}
                            className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700"
                        >
                            Accept Invitation
                        </button>
                        <button
                            onClick={() => handleAction("reject")}
                            className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700"
                        >
                            Reject Invitation
                        </button>
                    </div>
                </div>
            )}

            {status === "loading" && <p className="animate-pulse text-gray-600">Processing...</p>}

            {status === "success" && (
                <div>
                    <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <h2 className="text-lg text-green-700 font-semibold mb-2">{message}</h2>
                    {message.includes("already joined") ? (
                        <div className="mt-4">
                            <p className="text-gray-600 mb-3">Redirecting you to login in 5 seconds...</p>
                            <button
                                onClick={() => navigate("/login")}
                                className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700"
                            >
                                Go to Login
                            </button>
                        </div>
                    ) : (
                        <button
                            onClick={() => navigate(`/groups/${groupId}/${groupName}`)}
                            className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700"
                        >
                            Go to Group
                        </button>
                    )}
                </div>
            )}

            {status === "error" && (
                <div>
                    <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                    <h2 className="text-lg text-red-700 font-semibold mb-2">{message}</h2>
                    {message.includes("already joined") && (
                        <button
                            onClick={() => navigate("/login")}
                            className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 mt-3"
                        >
                            Go to Login
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default PublicGroupInvitePage;
